import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import { InlineMarkdown } from '@/components/core/markdown/markdown';
import type { OpenableSection } from '@/lib/lesson/textSections';
import { ReadCheckbox } from './ReadCheckbox';

/** Above a section: back to the contents, and where it sits in the lesson. */
export function TextSectionTop({
  section,
  total,
  onContents,
}: {
  section: OpenableSection;
  total: number;
  onContents: () => void;
}) {
  return (
    <div className='mb-6 flex items-center gap-3'>
      <Button
        variant='ghost'
        size='sm'
        onClick={onContents}
        className='-ml-2 h-8 gap-1.5 px-2 text-muted-foreground'
      >
        <ArrowLeft className='h-4 w-4' aria-hidden />
        All sections
      </Button>
      <span className='ml-auto font-mono text-xs tabular-nums text-muted-foreground/90'>
        Section {section.index} of {total}
      </span>
    </div>
  );
}

/** Below a section: the student's "read" mark, then the previous and the
 *  next section. The next is held while it is still being written. */
export function TextSectionBottom({
  read,
  onRead,
  previous,
  next,
  onOpen,
}: {
  read: boolean;
  onRead: (read: boolean) => void;
  previous: OpenableSection | null;
  next: OpenableSection | null;
  onOpen: (index: number) => void;
}) {
  return (
    <div className='mt-12 border-t border-foreground/10 pt-6'>
      <ReadCheckbox checked={read} onChange={onRead}>
        Mark this section as read
      </ReadCheckbox>
      {(previous || next) && (
        <nav aria-label='Sections' className='mt-6 grid gap-3 sm:grid-cols-2'>
          {previous ? (
            <SideLink
              label='Previous'
              section={previous}
              onOpen={onOpen}
              align='start'
            />
          ) : (
            <span className='hidden sm:block' />
          )}
          {next && (
            <SideLink label='Next' section={next} onOpen={onOpen} align='end' />
          )}
        </nav>
      )}
    </div>
  );
}

function SideLink({
  label,
  section,
  onOpen,
  align,
}: {
  label: string;
  section: OpenableSection;
  onOpen: (index: number) => void;
  align: 'start' | 'end';
}) {
  const end = align === 'end';
  return (
    <button
      type='button'
      onClick={() => onOpen(section.index)}
      disabled={!section.ready}
      className={`group flex flex-col gap-1 rounded-xl border border-foreground/10 p-4 transition-colors hover:border-brand-500/40 hover:bg-brand-500/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-70 disabled:hover:border-foreground/10 disabled:hover:bg-transparent dark:hover:border-primary/40 dark:hover:bg-primary/5 ${
        end ? 'items-end text-right' : 'items-start text-left'
      }`}
    >
      <span className='flex items-center gap-1.5 font-mono text-xs text-muted-foreground'>
        {!end && <ArrowLeft className='h-3.5 w-3.5' aria-hidden />}
        {label}
        {end &&
          (section.ready ? (
            <ArrowRight className='h-3.5 w-3.5' aria-hidden />
          ) : (
            <Loader2
              className='h-3.5 w-3.5 animate-spin motion-reduce:animate-none'
              aria-hidden
            />
          ))}
      </span>
      <InlineMarkdown className='text-sm font-semibold text-foreground'>
        {section.ready
          ? section.title
          : `${section.title} - still being written`}
      </InlineMarkdown>
    </button>
  );
}
